/**
 * The portal's chrome, matching the approved 2026-09-30 prototype (today-ui-prototype: style.css `.platform`, `.mode`,
 * `.rail`): the 52px IDC Platform bar (logo · divider · Software Tracker, then "Research workspace" and the initials
 * avatar), the BUILD / LIVE mode band (never animated, the mode from tracker-api's /api/portal/me; it scrolls with the
 * page), and the signed-in shell's 220px night rail: a "Workspace" label, the nav in labelled sections (Work,
 * Analysis, Admin) with line icons, Cycle and Settings as collapsible groups, and Help plus the person at the foot.
 * Under 650px the rail is a night bar whose "All pages" button drops the same nav over the page.
 *
 * In production the IDC Platform SDK's <idc-shell> element *is* the Platform bar (IDC mark, app name, apps launcher
 * and its own Sign out). When it is on the page the portal doesn't draw a second bar: it slots its BUILD tag (and,
 * on phones, a Sign out icon) into the SDK bar's "actions" slot. Without the SDK (tests, local harness) it draws the
 * same bar itself, with Sign out as an icon after the avatar.
 */
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { isAdmin, isManager, roleLabel } from "../roles.ts";
import { Link, useRoute, useRouteHash } from "../router.tsx";
import { routeHref, withReturn } from "../screens/navigationContext.ts";
import { LineIcon, type LineIconName } from "./LineIcon.tsx";
import { NotificationBell, NotificationsDrawer } from "./Notifications.tsx";
import { Icon, IdcLogo, Sprites } from "./Sprites.tsx";
import { ToastProvider } from "./Toast.tsx";
import "../styles/ipd.css";
import "../styles/portal.css";
import "./workbench-navigation.css";

export const APP_NAME = "Software Tracker";
export type Mode = "build" | "live";

/**
 * count: the rail badge (GET /me/counts), shown only when above zero; null shows "—" (the counts failed to load).
 * A `to` with a #hash points at a panel and takes precedence over its page when that hash is current.
 * An item with `children` is a collapsible group (Cycle, Settings): its own page is the first child ("Overview").
 */
export interface NavItem { to: string; label: string; section: string; icon?: LineIconName; count?: number | null; children?: NavItem[] }

/** A labelled group of rail links (AIC-257): Work, Analysis and, for admins, Admin. */
export interface NavSection { id: string; label: string; items: NavItem[] }

/** Another module's rail and bar in place of the Software Tracker's (IDC Notetaker, AIC-1437). */
export interface ShellModule { name: string; nav: NavSection[]; title: string; role: string }
/** A temporary link to another module in the rail's foot (Notetaker until the Workspace navigation lands, D9). */
export interface ShellEntry { to: string; label: string }

/** The rail's count (prototype): Review = the caller's open review items. null = unavailable ("—"). */
export interface NavCountsView { review?: number | null }

/**
 * The rail, by role, in sections (AIC-257). Work and Analysis are every portal role's (admin, manager, analyst; the
 * API scopes what each sees, AIC-145, AIC-148). Admin is admins' only (AIC-180): People (AIC-556, which replaced Analysts), Cycle (its overview, the
 * SharePoint imports and the Survey_list reconciliation, AIC-95) and Settings (program defaults and Retention).
 * Managers can open the reconciliation too, but not Admin or Cycle, so for them it is in a Manager section of its own.
 */
export function navFor(roles: readonly string[], counts?: NavCountsView | null, practiceAssignment?: string | null): NavSection[] {
  const sections: NavSection[] = [
    ...(practiceAssignment === undefined ? [] : [{
      id: "practice", label: "Practice", items: [
        ...(practiceAssignment ? [{ to: `/assignments/${encodeURIComponent(practiceAssignment)}`, label: "Practice assignment", section: "assignments", icon: "list" as const }] : []),
        { to: "/practice/outbox", label: "Practice outbox", section: "practice", icon: "arrow" as const },
        { to: "/practice", label: "Practice sessions", section: "practice", icon: "check" as const },
      ],
    }]),
    {
      id: "work", label: "Work", items: [
        { to: "/today", label: "Today", section: "today", icon: "today" },
        { to: "/assignments", label: "Assignments", section: "assignments", icon: "list" },
        { to: "/review", label: "Review", section: "review", icon: "check", count: counts?.review },
      ],
    },
    { id: "analysis", label: "Analysis", items: [{ to: "/insights", label: "Insights", section: "insights", icon: "chart" }, { to: "/dashboard", label: "Cycle dashboard", section: "dashboard", icon: "chart" }] },
  ];
  const reconciliation: NavItem = { to: "/admin/cycle/reconciliation", label: "Reconciliation", section: "admin" };
  if (isAdmin(roles)) {
    sections.push({
      id: "admin", label: "Admin", items: [
        { to: "/admin/people", label: "People", section: "admin", icon: "people" },
        { to: "/admin/cycle", label: "Cycle", section: "admin", icon: "cycle", children: [
          { to: "/admin/cycle", label: "Overview", section: "admin" },
          { to: "/admin/cycle/imports", label: "Imports", section: "admin" },
          reconciliation,
        ] },
        { to: "/admin/settings", label: "Settings", section: "admin", icon: "settings", children: [
          { to: "/admin/settings", label: "Program defaults", section: "admin" },
          { to: "/admin/settings#retention", label: "Retention", section: "admin" },
        ] },
      ],
    });
  } else if (isManager(roles)) {
    sections.push({ id: "manager", label: "Manager", items: [{ ...reconciliation, icon: "cycle" }] });
  }
  return sections;
}

/** Every link the rail renders (a group's own entry is its first child, so the group itself is skipped). */
const links = (items: readonly NavItem[]): NavItem[] => items.flatMap((i) => (i.children?.length ? links(i.children) : [i]));

/** The one nav link a path marks current: the longest match ("/" only exactly; results belong to Review). */
export function currentNav(path: string, sections: readonly NavSection[]): string | null {
  const p = path.startsWith("/results") ? "/review" : path;
  let best: string | null = null;
  for (const { to } of links(sections.flatMap((s) => s.items))) {
    if (to.includes("#")) { if (p === to) best = to; continue; }
    const hit = to === "/" ? p === "/" : p.split("#")[0] === to || p.startsWith(`${to}/`);
    if (hit && (!best || to.length > best.length)) best = to;
  }
  return best;
}

function NavCount({ count }: { count?: number | null }) {
  if (count === null) return <span className="ipd-nav-count"><span className="ipd-sr">, count unavailable</span><span aria-hidden="true">—</span></span>;
  if (!count) return null;
  return <span className="ipd-nav-count"><span className="ipd-sr">, </span>{count}<span className="ipd-sr"> open</span></span>;
}

function NavLink({ item, current }: { item: NavItem; current: string | null }) {
  return (
    <Link to={item.to} className="ipd-nav-link" aria-current={current === item.to ? "page" : undefined}>
      {item.icon ? <LineIcon name={item.icon} /> : null}
      {item.label}
      <NavCount count={item.count} />
    </Link>
  );
}

/**
 * Cycle / Settings: a <details> group (prototype `.navgroup`). It opens by itself when the current page is inside it,
 * and otherwise keeps whatever the user last chose. Its children are indented to the label.
 */
function NavGroup({ item, current }: { item: NavItem; current: string | null }) {
  const inside = (item.children ?? []).some((c) => c.to === current);
  const [open, setOpen] = useState(inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  return (
    <details className={`ipd-navgroup${inside ? " is-current" : ""}`} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        {item.icon ? <LineIcon name={item.icon} /> : null}
        {item.label}

      </summary>
      <ul className="ipd-nav-sub">
        {item.children!.map((c) => <li key={c.to + c.label}><Link to={c.to} aria-current={current === c.to ? "page" : undefined}>{c.label}</Link></li>)}
      </ul>
    </details>
  );
}

/** The rail's sections: each a list named by its small uppercase label. */
function NavSections({ sections, current }: { sections: readonly NavSection[]; current: string | null }) {
  return (
    <div className="ipd-nav-secs">
      {sections.map((s) => {
        const id = `nav-${s.id}`;
        return (
          <div key={s.id} className="ipd-nav-sec" data-section={s.id}>
            <span className="ipd-nav-label" id={id}>{s.label}</span>
            <ul aria-labelledby={id}>
              {s.items.map((n) => <li key={n.to + n.label}>{n.children?.length ? <NavGroup item={n} current={current} /> : <NavLink item={n} current={current} />}</li>)}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export const HELP_LABEL = "Help & getting started";

/**
 * Help → Getting started (AIC-305), for every portal role, at the foot of the rail. It carries the page it was opened
 * from as a local return (navigationContext.ts), so Getting started can offer a way back.
 */
function HelpLink({ path }: { path: string }) {
  // The whole current location, filters included, so the way back restores the page as it was.
  const route = useRoute();
  return (
    <Link to={path === "/help" ? "/help" : withReturn("/help", routeHref(route))} className="ipd-nav-link ipd-rail-help" data-onboarding="help-link" aria-current={path === "/help" ? "page" : undefined}>
      <LineIcon name="help" />{HELP_LABEL}
    </Link>
  );
}

/** Which nav section a path belongs to. including the legacy Cycle dashboard. */
export function sectionOf(path: string): string {
  if (path === "/") return "home";
  if (path === "/today") return "today";
  if (path.startsWith("/assignments")) return "assignments";
  if (path.startsWith("/review") || path.startsWith("/results")) return "review";
  if (path.startsWith("/dashboard")) return "dashboard";
  if (path.startsWith("/insights")) return "insights";
  if (path.startsWith("/admin")) return "admin";
  if (path === "/help") return "help";
  if (path === "/profile" || path.startsWith("/profile/")) return "profile";
  if (path.startsWith("/practice")) return "practice";
  return "";
}

const TITLES: Readonly<Record<string, string>> = {
  home: "Analyst Workbench", today: "Today", assignments: "Assignments", review: "Review", dashboard: "Dashboard", insights: "Insights", admin: "Admin", help: "Getting started", practice: "Practice", profile: "My profile",
};

/** "Bill Marino" → "BM"; an address → its first letter. */
export function initialsOf(name: string, email = ""): string {
  const words = (name || email.split("@")[0] || "").split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words.length > 1 ? words.at(-1)![0] : "")).toUpperCase() || "?";
}

/** The SDK's <idc-shell> element, when the SDK has mounted it (it prepends it to <body> once signed in). */
function useSdkBar(): Element | null {
  return useSyncExternalStore(
    (fn) => {
      if (typeof MutationObserver === "undefined" || !document.body) return () => {};
      const mo = new MutationObserver(fn);
      mo.observe(document.body, { childList: true });
      return () => mo.disconnect();
    },
    () => document.querySelector("idc-shell"),
    () => null,
  );
}

const BuildTag = ({ name = APP_NAME }: { name?: string }) => <span className="ipd-bar-env" title={`${name} is in build mode`}>BUILD</span>;
/** AIC-463: beside BUILD, in its own colour, while a practice session is active. Shown in live mode too. */
const PracticeTag = () => <span className="ipd-bar-env ipd-bar-env--practice" title="You are in a practice session: nothing reaches vendors, Outlook or the Data Layer">PRACTICE</span>;
const SignOut = ({ onSignOut }: { onSignOut: () => void }) => (
  <button type="button" className="ipd-bar-out" aria-label="Sign out" title="Sign out" onClick={onSignOut}><Icon name="exit" /></button>
);

export interface Person { name: string; email: string }

/**
 * The Platform bar: the SDK's (with our actions slotted in) or our own. onSignOut and person only with a session.
 * `bell` (AIC-511): the notifications bell, before the avatar in our bar and before Sign out in the SDK's slot.
 */
function PlatformBar({ mode, onSignOut, person, practice = false, bell, appName = APP_NAME, workspaceOnly = false }: { mode: Mode; onSignOut?: () => void; person?: Person; practice?: boolean; bell?: ReactNode; appName?: string; workspaceOnly?: boolean }) {
  const sdk = useSdkBar();
  if (sdk) return createPortal(<span slot="actions" className="ipd-slot">{mode === "build" ? <BuildTag name={appName} /> : null}{practice ? <PracticeTag /> : null}{bell}{onSignOut ? <SignOut onSignOut={onSignOut} /> : null}</span>, sdk);
  return (
    <header className="ipd-bar">
      <div className="ipd-bar-brand">
        <IdcLogo />
        <span className="ipd-bar-divider" aria-hidden="true" />
        <b>{appName}</b>
        {mode === "build" ? <BuildTag name={appName} /> : null}
        {practice ? <PracticeTag /> : null}
      </div>
      <div className="ipd-bar-right">
        {person ? <>
          <span className="ipd-bar-context">Research workspace</span>
          {bell}
          {/* AIC-815: the avatar opens My profile (the SDK's own bar has no avatar of ours: the rail's person tile is its way in). */}
          {workspaceOnly ? null : <Link to="/profile" className="ipd-avatar ipd-avatar--link" title="My profile" aria-label={`My profile, ${person.name || person.email}`}>{initialsOf(person.name, person.email)}</Link>}
        </> : null}
        {onSignOut ? <SignOut onSignOut={onSignOut} /> : null}
      </div>
    </header>
  );
}

/** Safety chrome: on every screen, can't be dismissed, never animates, no toggle. */
export function ModeBand({ mode, tester }: { readonly mode: Mode; readonly tester?: string | null }) {
  if (mode === "live") {
    return (
      <div className="ipd-mode ipd-mode--live" role="region" aria-label="System mode">
        <span className="ipd-mode-tag">LIVE</span>
        <p>Invite and reminder drafts go to your Outlook Drafts — you review and send every email.</p>
      </div>
    );
  }
  return (
    <div className="ipd-mode ipd-mode--build" role="region" aria-label="System mode">
      <b className="ipd-mode-tag">BUILD MODE</b>
      <p>
        <strong>Nothing is emailed.</strong> Drafts go to the build tester only. Interview links and codes are for IDC staff testing.
        {tester ? <> Build tester: <b>{tester}</b>.</> : null}
      </p>
    </div>
  );
}

/**
 * The bar and mode band scroll away with the page (prototype), so fixed and sticky page chrome (drawers, the results
 * sheet, sticky table heads) must sit below whatever part of them is still on screen, and nowhere lower once they
 * have gone. --ipd-band-h carries that: the visible chrome under --ipd-top, from the band's bottom edge (0 once
 * scrolled past). It is written only when it changes, so a scrolled page does no style work.
 */
function useVisibleChrome(root: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el || typeof window === "undefined") return;
    let last = -1, frame = 0;
    const measure = () => {
      frame = 0;
      const band = el.querySelector(":scope > .ipd-mode");
      const top = parseFloat(getComputedStyle(el).getPropertyValue("--ipd-top")) || 0;
      const v = band ? Math.max(0, Math.round(band.getBoundingClientRect().bottom - top)) : 0;
      if (v !== last) { last = v; el.style.setProperty("--ipd-band-h", `${v}px`); }
    };
    const queue = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    return () => { window.removeEventListener("scroll", queue); window.removeEventListener("resize", queue); if (frame) cancelAnimationFrame(frame); };
  }, [root]);
}

/**
 * The app root on every screen: .ipd-app with the mode, the sprites, the skip link (to this screen's visible
 * <main>), the Platform bar and the mode band.
 */
export function Frame({ mode, skipTo, onSignOut, person, tester, practice = false, bell, appName, workspaceOnly = false, children }: { mode: Mode; skipTo: string; onSignOut?: () => void; person?: Person; tester?: string | null; practice?: boolean; bell?: ReactNode; appName?: string; workspaceOnly?: boolean; children: ReactNode }) {
  const sdk = useSdkBar();
  const root = useRef<HTMLDivElement>(null);
  useVisibleChrome(root);
  return (
    <div className={`ipd-app${sdk ? " has-sdk-bar" : ""}`} data-mode={mode} ref={root}>
      <Sprites />
      <a className="ipd-skip" href={`#${skipTo}`}>Skip to main content</a>
      <PlatformBar mode={mode} onSignOut={onSignOut} person={person} practice={practice} bell={bell} appName={appName} workspaceOnly={workspaceOnly} />
      {workspaceOnly ? null : <ModeBand mode={mode} tester={tester} />}
      {/* Inside .ipd-app, so the toast stack gets the app's type and its reduced-motion fallback. */}
      <ToastProvider>{children}</ToastProvider>
    </div>
  );
}

export function Shell({ path, name, email, roles, buildMode, buildTester, counts, notifications = null, countsRead, onSignOut, notice, practice = null, module, entry, workspaceOnly = false, children }: {
  path: string; name: string; email: string; roles: readonly string[]; buildMode: boolean; onSignOut: () => void; children: ReactNode;
  /** The build tester's name from /me (AIC-181), on the BUILD band. */
  buildTester?: string | null;
  /** The rail's count from GET /me/counts (AIC-181); none shown until it loads, "—" when it failed. */
  counts?: NavCountsView | null;
  /** A band across the content column above <main> (the analyst-preview band), so page mastheads stay flush. */
  notice?: ReactNode;
  /** AIC-463: a practice session is active: the PRACTICE tag beside BUILD in the Platform bar. */
  practice?: { assignmentId: string } | null;
  /**
   * AIC-511: the bell's unread count (GET /me/counts' notifications). null or absent: no bell (analyst preview, practice,
   * a server without migration 0058, or the counts haven't loaded).
   */
  notifications?: number | null;
  /** Changes with every /me/counts answer (the answer itself will do), even when the count is the same number. */
  countsRead?: unknown;
  /** AIC-1437: another module's rail, bar name, page title and role label (IDC Notetaker); absent, the Software Tracker's. */
  module?: ShellModule;
  /** AIC-1437: a link to another module in the rail's foot, above Help. */
  entry?: ShellEntry;
  /**
   * AIC-1486 (Bill, 2026-10-22): a person without a Software Tracker role. No mode band, and no Home, Help, My profile
   * tile or avatar link, which are Software Tracker pages.
   */
  workspaceOnly?: boolean;
}) {
  const hash = useRouteHash();
  const [pagesOpen, setPagesOpen] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);
  const bellShown = typeof notifications === "number" && practice === null;
  // Marked read in the drawer: nothing new until the next counts answer, whatever number it gives (the same number again
  // means the mark didn't take, or more arrived: either way the server's count is the truth).
  const [quiet, setQuiet] = useState(false);
  useEffect(() => setQuiet(false), [notifications, countsRead]);
  // Following an item (or any navigation) closes the drawer; so does losing the bell (preview, practice).
  useEffect(() => setNotesOpen(false), [path]);
  useEffect(() => { if (!bellShown) setNotesOpen(false); }, [bellShown]);
  const workbench = path === "/";
  useEffect(() => setPagesOpen(false), [path, hash]);
  const rail = useRef<HTMLElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  // "All pages" (phones) closes on a press anywhere outside the rail, and on Escape, which returns focus to it.
  useEffect(() => {
    if (!pagesOpen) return;
    const outside = (e: PointerEvent) => { if (e.target instanceof Node && !rail.current?.contains(e.target)) setPagesOpen(false); };
    // Capture, so it runs before the tour's own Escape and can claim the key.
    const escape = (e: KeyboardEvent) => { if (e.key !== "Escape" || e.defaultPrevented) return; e.preventDefault(); setPagesOpen(false); toggle.current?.focus({ preventScroll: true }); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape, true); };
  }, [pagesOpen]);
  const current = sectionOf(path);
  // In practice the rail leads with the practice pages; the real pages stay listed but open the "not available" card.
  const nav = module?.nav ?? navFor(roles, practice ? null : counts, practice ? practice.assignmentId : undefined);
  const main = useRef<HTMLElement>(null);
  const shownPath = useRef(path);
  useEffect(() => {
    document.title = module ? `${module.title} · ${module.name}` : path === "/admin/cycle/reconciliation" ? `Reconciliation · ${APP_NAME}` : path === "/admin/people" ? `People · ${APP_NAME}` : TITLES[current] ? `${TITLES[current]} · ${APP_NAME}` : APP_NAME;
    // After a route change (not on the first render, StrictMode's re-run included), focus moves to the new <main>.
    if (shownPath.current === path) return;
    shownPath.current = path;
    main.current?.focus({ preventScroll: true });
  }, [path, current, module?.title, module?.name]);
  return (
    <Frame mode={buildMode ? "build" : "live"} skipTo="main" onSignOut={onSignOut} person={{ name, email }} tester={buildMode ? buildTester : null} practice={practice !== null} appName={module?.name} workspaceOnly={workspaceOnly}
      bell={bellShown ? <NotificationBell count={quiet ? 0 : notifications ?? 0} open={notesOpen} onOpen={() => setNotesOpen(true)} /> : null}>
      <div className="ipd-shell">
        <aside className={`ipd-rail ipd-rail--${workbench ? "workbench" : "tracker"}${pagesOpen ? " is-open" : ""}`} aria-label={module?.name ?? APP_NAME} ref={rail}>
          <div className="ipd-rail-top">
            <span>{workbench ? "Your workspace" : "Workspace"}</span>
            <button type="button" className="ipd-rail-toggle" ref={toggle} aria-expanded={pagesOpen} aria-controls="rail-pages" onClick={() => setPagesOpen(!pagesOpen)}>{workbench ? "Menu" : "All pages"}</button>
          </div>
          <div className="ipd-rail-pages" id="rail-pages">
            <nav className="ipd-nav" aria-label="Main">
              {workbench ? <>
                <Link to="/" className="ipd-nav-link ipd-home-link" aria-current={!["#myDay", "#inbox"].includes(hash) ? "page" : undefined}><LineIcon name="home" />Home</Link>
                <Link to="/#myDay" className="ipd-nav-link" aria-current={hash === "#myDay" ? "page" : undefined}><LineIcon name="today" />My day</Link>
                <Link to="/#inbox" className="ipd-nav-link" aria-current={hash === "#inbox" ? "page" : undefined}><LineIcon name="list" />My inbox</Link>
                <details className="ipd-workbench-app" open={trackerOpen} onToggle={e => setTrackerOpen(e.currentTarget.open)}>
                  <summary><LineIcon name="chart" />Software Tracker</summary>
                  <ul aria-label="Software Tracker">
                    {nav[0]!.items.map(item => <li key={item.to}><NavLink item={{ ...item, icon: undefined }} current={null} /></li>)}
                  </ul>
                </details>
              </> : <NavSections sections={nav} current={currentNav(path + hash, nav)} />}
            </nav>
            {workbench ? <div className="ipd-workbench-rail-note"><b>Your work. Your order.</b><p>Make a little room for the decisions that matter today.</p></div> : null}
            <div className="ipd-rail-foot">
              {!workbench && !workspaceOnly ? <Link to="/" className="ipd-nav-link ipd-home-link"><LineIcon name="home" />Home</Link> : null}
              {entry ? <Link to={entry.to} className="ipd-nav-link" data-testid="module-entry"><LineIcon name="arrow" />{entry.label}</Link> : null}
              {workspaceOnly ? null : <>
              <HelpLink path={path} />
              {/* AIC-815: the person tile opens My profile; on it, the tile is the current page. */}
              <Link to="/profile" className="ipd-person" aria-current={path === "/profile" ? "page" : undefined} title="My profile">
                <span className="ipd-avatar" aria-hidden="true">{initialsOf(name, email)}</span>
                <div><b title={email}>{name || email}</b><small>{module?.role ?? roleLabel(roles)}{path === "/profile" ? " · My profile" : ""}</small></div>
              </Link>
              </>}
            </div>
          </div>
        </aside>
        <div className="ipd-main">
          {notice}
          <main className="ipd-content" id="main" tabIndex={-1} ref={main}>{children}</main>
        </div>
      </div>
      {bellShown && notesOpen ? <NotificationsDrawer onClose={() => setNotesOpen(false)} onSeen={() => setQuiet(true)} /> : null}
    </Frame>
  );
}

/** A centred card: pre-sign-in states (signing in, no access, unavailable) and in-shell dead ends. */
export function Standalone({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="ipd-card ipd-standalone" aria-labelledby="standalone-h">
      <h1 id="standalone-h">{title}</h1>
      {children}
    </section>
  );
}

/** A pre-sign-in page: the frame (bar + band) around a centred card. */
export function PublicPage({ mode, title, workspaceName, children }: { mode: Mode; title: string; workspaceName?: string; children: ReactNode }) {
  // AIC-1487: workspaceName is the neutral boot page (Notetaker flag on, who the user is not yet known): that name is the
  // tab title and the bar's name, and the Software Tracker's mode band stays out of it, as in WorkspaceOnly.
  useEffect(() => { document.title = workspaceName ?? `${title} · ${APP_NAME}`; }, [title, workspaceName]);
  return (
    <Frame mode={mode} skipTo="main-pub" appName={workspaceName} workspaceOnly={workspaceName !== undefined}>
      <main className="ipd-public" id="main-pub" tabIndex={-1}>
        <Standalone title={title}>{children}</Standalone>
      </main>
    </Frame>
  );
}
